import { createApolloProvider } from "@vue/apollo-option";
import { RestLink } from "apollo-link-rest";
import {
  ApolloClient,
  InMemoryCache,
  from,
  createHttpLink,
  ApolloLink,
  type DefaultContext,
} from "@apollo/client";
import { LocalStorageKeys, SessionStorageKeys } from "@/enums";

const queryParams = new URLSearchParams(window.location.search);

// Name of the localStorage item
const AUTH_TOKEN = "apollo-token";

// Http endpoint
let httpEndpoint;

if (
  !import.meta.env.VITE_APP_GRAPHQL_HTTP &&
  import.meta.env.VITE_APP_API_URL
) {
  httpEndpoint = new URL(import.meta.env.VITE_APP_API_URL + "/graphql");
} else {
  httpEndpoint = new URL(
    import.meta.env.VITE_APP_GRAPHQL_HTTP ||
      "https://api.ibgmanager.dk/ibg/graphql",
  );
}

const getInstitutionId = () => {
  const institutionId = queryParams.get("institution_id");
  if (institutionId && institutionId.length > 0) {
    return institutionId;
  }

  try {
    const authModuleData = JSON.parse(
      sessionStorage.getItem(SessionStorageKeys.AuthModule) || "{}",
    );
    return authModuleData.currentInstitutionId;
  } catch (error) {
    return undefined;
  }
};

const institutionId = getInstitutionId();

if (!httpEndpoint.searchParams.has("institution_id") && institutionId) {
  httpEndpoint.searchParams.append("institution_id", institutionId);
}

// Files URL root
export const filesRoot =
  import.meta.env.VITE_APP_FILES_ROOT ||
  httpEndpoint
    .toString()
    .substr(0, httpEndpoint.toString().indexOf("/graphql"));

const authLink = new ApolloLink((operation, forward) => {
  const authModuleRaw = sessionStorage.getItem(
    SessionStorageKeys.AuthModule,
  ) as string;
  const authModule = JSON.parse(authModuleRaw);

  const token =
    sessionStorage.getItem(LocalStorageKeys.LoopbackAccessTokenId) ||
    localStorage.getItem(LocalStorageKeys.LoopbackAccessTokenId) ||
    authModule?.token ||
    import.meta.env.VITE_APP_DEV_TOKEN;

  operation.setContext(({ headers }: Partial<DefaultContext>) => ({
    headers: {
      ...headers,
      authorization: token ? token : "",
      "IBG-Channel": "manager",
    },
  }));

  return forward(operation);
});

const httpLink = createHttpLink({
  uri: httpEndpoint.toString(),
});

const restLink = new RestLink({
  uri: import.meta.env.VITE_APP_API_URL || "https://api.ibgmanager.dk/ibg",
  // Remove trailing slashes in all GraphQL requests,
  // which may occur when the last passed in argument is empty string.
  customFetch: (info: RequestInfo, options: RequestInit) => {
    const uri = info.toString();
    const url = uri.replace(/\/+$/, "");
    const urlRequest = new URL(url);

    const institutionId = getInstitutionId();
    if (!urlRequest.searchParams.has("institution_id") && institutionId) {
      urlRequest.searchParams.append("institution_id", institutionId);
    }
    return fetch(urlRequest.toString(), options);
  },
});

// Config
const defaultOptions = {
  // You can use `https` for secure connection (recommended in production)
  httpEndpoint: httpEndpoint.toString(),
  // You can use `wss` for secure connection (recommended in production)
  // Use `null` to disable subscriptions
  wsEndpoint: null,
  // LocalStorage token
  tokenName: AUTH_TOKEN,
  // Enable Automatic Query persisting with Apollo Engine
  persisting: false,
  // Use websockets for everything (no HTTP)
  // You need to pass a `wsEndpoint` for this to work
  websocketsOnly: false,
  // Is being rendered on the server?
  ssr: false,

  // Override default apollo link
  // note: don't override httpLink here, specify httpLink options in the
  // httpLinkOptions property of defaultOptions.

  // link: authLink.concat(restLink),
  link: from([authLink, restLink, httpLink]),

  // Override default cache
  cache: new InMemoryCache(),

  // Override the way the Authorization header is set
  // getAuth: (tokenName) => ...

  // Additional ApolloClient options
  // apollo: { ... }

  // Client local data (see apollo-link-state)
  // clientState: { resolvers: { ... }, defaults: { ... } }
};

// Call this in the Vue app file
export function createProvider(options = {}) {
  // Create apollo client
  const apolloClient = new ApolloClient({
    ...defaultOptions,
    ...options,
    devtools: {
      enabled: false,
    },
    defaultOptions: {
      query: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
      },
      mutate: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
      },
      watchQuery: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
      },
    },
  });

  // Create vue apollo provider
  const apolloProvider = createApolloProvider({
    defaultClient: apolloClient,
    errorHandler(error) {
      console.log(
        "%cError",
        "background: red; color: white; padding: 0.125rem; border-radius: 0.1875rem; font-weight: bold;",
        error.message,
      );
    },
  });

  return apolloProvider;
}
